// My Shop Pub -> slider-shop -> "Main Shop Slider Source".
//   Option A -- Existing Image Slider: the historical uploaded images (below)
//   Option B -- Elakay Swiper: one of the user's own swipers (Swiper Studio),
//               or a Family swiper shared with their role
// The chosen swiper becomes the Hero Swiper at the top of the shop, instead
// of the image slider (never both). Permissions and quotas come from
// Animation Settings -> Swiper Access & Limits; the server re-checks them.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaExchangeAlt, FaExternalLinkAlt, FaPlus, FaSave, FaSpinner, FaTrash } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";
import { getShopHero, setShopHero } from "../auth/apiDynamicSwipers";
import SwiperLibraryPicker from "./SwiperLibraryPicker";
import { DevicePreview, Segmented, SwiperThumb, errorMessage } from "./SwiperAdminUI";
import { canAccessSwiperManager, useSwiperAccess } from "./useSwiperAccess";
import { studioUrl } from "./studioTabs";
import "./SwiperAdmin.css";

const text = elakayAutoText;
const MODE_GALLERY = "gallery";
const MODE_SWIPER = "swiper";

const ShopHeroSourcePanel = ({ infoSource, shopUrl = null }) => {
  const { access } = useSwiperAccess(infoSource);
  const [state, setState] = useState({ loading: true, error: null, hero: null, canPlace: false });
  const [mode, setMode] = useState("gallery");
  const [chosen, setChosen] = useState(null); // full library swiper
  const [picking, setPicking] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const flash = (type, message) => setNotice({ type, message });

  const apply = (hero) => {
    setMode(hero?.source === MODE_SWIPER ? MODE_SWIPER : MODE_GALLERY);
    setChosen(hero?.swiper ? { ...hero.swiper, id: hero.swiperId } : (hero?.swiperId ? { id: hero.swiperId, name: "", slides: [] } : null));
  };

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await getShopHero();
      setState({ loading: false, error: null, hero: result.hero, canPlace: result.canPlace === true });
      apply(result.hero);
    } catch (error) {
      setState({ loading: false, error, hero: null, canPlace: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = state.hero;
  const dirty = saved
    ? (mode !== saved.source || (mode === "swiper" && chosen?.id !== saved.swiperId))
    : mode === "swiper";
  const canOpenManager = canAccessSwiperManager(access);
  const canCreate = canOpenManager && access.owned < access.limits?.maxSwipers;

  const save = async (source = mode, swiperId = source === "swiper" ? chosen?.id : undefined) => {
    setSaving(true);
    setNotice(null);
    try {
      const result = await setShopHero(source, swiperId);
      setState((prev) => ({ ...prev, hero: result.hero }));
      apply(result.hero);
      flash("success", result.hero.source === "swiper"
          ? text("h_shero_saved_swiper", "\"{name}\" is now the main slider at the top of your shop.", { name: result.hero.swiper?.name || chosen?.name || "" })
          : text("h_shero_saved_gallery", "Your shop shows its image slider again."),
      );
    } catch (error) {
      flash("error", errorMessage(error, text("h_shero_save_error", "The main slider could not be saved.")));
    } finally {
      setSaving(false);
    }
  };

  if (state.loading && !saved) {
    return <div className="dswa pub-section-card" role="status"><FaSpinner className="dswa-spin" aria-hidden="true" /> {text("h_shero_loading", "Loading your main slider…")}</div>;
  }

  return (
    <div className="dswa pub-section-card shero" data-testid="shop-hero-source">
      <div className="dswa-compose-head">
        <div>
          <h3 className="dswa-panel-title"><span>{text("h_shero_title", "Main Shop Slider Source")}</span></h3>
          <p className="dswa-hint" style={{ margin: "4px 0 0" }}>
            {text("h_shero_hint", "The large slider at the top of your shop: your uploaded images, or an Elakay Swiper built in the Swiper Studio (colors, texts, animations, products, buttons...). Only one is shown.")}
          </p>
        </div>
        <div className="dswa-actions">
          {shopUrl && (
            <Link className="dswa-btn" to={shopUrl} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt aria-hidden="true" /> {text("h_shero_view_shop", "View my shop")}
            </Link>
          )}
          <button type="button" className="dswa-btn dswa-btn-primary" onClick={() => save()} disabled={!dirty || saving || (mode === "swiper" && !chosen)}>
            {saving ? <FaSpinner className="dswa-spin" aria-hidden="true" /> : <FaSave aria-hidden="true" />}
            {text("h_shero_save", "Save")}
          </button>
        </div>
      </div>
      {state.error && <div className="dswa-notice dswa-notice-error" role="alert"><span>{errorMessage(state.error, text("h_shero_load_error", "Your main slider could not be loaded."))}</span></div>}
      {notice && <div className={`dswa-notice dswa-notice-${notice.type}`} role={notice.type === "error" ? "alert" : "status"}><span>{notice.message}</span></div>}

      <Segmented
        value={mode}
        onChange={(value) => { if (value === "swiper" && !state.canPlace) return; setMode(value); setNotice(null); }}
        label={text("h_shero_title", "Main Shop Slider Source")}
        options={[
          { value: "gallery", label: text("h_shero_mode_gallery", "Existing Image Slider") },
          { value: "swiper", label: text("h_shero_mode_swiper", "Elakay Swiper Studio") },
        ]}
      />
      {!state.canPlace && (
        <div className="dswa-notice dswa-notice-info" role="note">
          <span>{text("h_shero_disabled", "Using an Elakay Swiper in your shop is not enabled for your role yet. Developer can enable it in Animation Settings → Swiper Access & Limits. Your image slider keeps working.")}</span>
        </div>
      )}

      {mode === "gallery" && (
        <p className="dswa-hint" style={{ margin: 0 }}>
          {saved?.source === "swiper"
            ? text("h_shero_gallery_pending", "Save to show your image slider again (your swiper is kept in the Swiper Manager).")
            : text("h_shero_gallery_active", "Your uploaded images below are shown at the top of your shop.")}
        </p>
      )}

      {mode === "swiper" && state.canPlace && (
        <div className="gpub-swiper">
          {chosen ? (
            <div className="gpub-swiper-card">
              <SwiperThumb url={chosen.slides?.[0]?.imageUrl} aspectRatio={chosen.aspectRatio} slide={chosen.slides?.[0]} />
              <div className="gpub-swiper-meta">
                <strong>{chosen.name || "—"}</strong>
                {chosen.source === "family" && <span className="dswa-badge dswa-badge-info">{text("h_esb_family_shared", "Shared by Developer")}</span>}
                {chosen.id === saved?.swiperId && saved?.source === "swiper"
                  ? <span className="dswa-badge dswa-badge-active">{text("h_shero_current", "Shown in your shop")}</span>
                  : <span className="dswa-badge dswa-badge-warning">{text("h_gpub_not_saved", "Not saved yet")}</span>}
              </div>
              <div className="dswa-actions">
                <button type="button" className="dswa-btn dswa-btn-sm" onClick={() => setPreviewing((open) => !open)} aria-pressed={previewing ? "true" : "false"} disabled={!chosen.slides?.length}>
                  {text("h_dsw_preview", "Preview")}
                </button>
                <button type="button" className="dswa-btn dswa-btn-sm" onClick={() => setPicking(true)}>
                  <FaExchangeAlt aria-hidden="true" /> {text("h_gpub_replace", "Replace")}
                </button>
                <button type="button" className="dswa-btn dswa-btn-sm dswa-btn-danger" onClick={() => save("gallery", null)} disabled={saving}>
                  <FaTrash aria-hidden="true" /> {text("h_gpub_remove", "Remove")}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="dswa-add-here" onClick={() => setPicking(true)}>
              {text("h_shero_select", "Select one of your swipers")}
            </button>
          )}
          <div className="dswa-actions">
            {canCreate && (
              <Link className="dswa-btn dswa-btn-sm" to={studioUrl(null)}>
                <FaPlus aria-hidden="true" /> {text("h_shero_create", "Create a swiper in the Swiper Studio")}
              </Link>
            )}
            {canOpenManager && <Link className="dswa-btn dswa-btn-sm dswa-btn-ghost" to="/elakay/swipers">{text("h_dsw_open_manager", "Open the Swiper Manager")}</Link>}
          </div>
          {previewing && chosen?.slides?.length > 0 && (
            <DevicePreview swiper={chosen} device={device} onDeviceChange={setDevice} />
          )}
        </div>
      )}

      <SwiperLibraryPicker
        show={picking}
        context="shop"
        selectedId={chosen?.id || null}
        title={text("h_shero_picker_title", "Choose the main slider of your shop")}
        onClose={() => setPicking(false)}
        onPick={(swiper) => { setPicking(false); setChosen(swiper); setMode(MODE_SWIPER); setNotice(null); }}
      />
    </div>
  );
};

export default ShopHeroSourcePanel;
